import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, Pagination, Button, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ImageIcon from '@mui/icons-material/Image';
import KenLoader from '../../../../GlobalComponents/KenLoader';

interface FileRecord {
  Id: string;
  Name: string;
  File_Link__c: string;
  CreatedDate: string;
  File_Admin__r?: {
    Profile_Picture_URL__c: string;
  };
}

interface CollaboratorRecord {
  Id: string;
  Contact__r: {
    Name: string;
    Profile_Picture_URL__c: string;
  };
}

interface FolderData {
  Id: string;
  Name: string;
  Folder_Type__c: string;
  Folder_Access__c: string;
  Cover_Image_Url__c?: string;
  CreatedDate: string;
  LastModifiedDate: string;
  Collaborators_Count__c: number;
  Files__r?: {
    totalSize: number;
    records: FileRecord[];
  };
  Folder_Collaborators__r?: {
    totalSize: number;
    records: CollaboratorRecord[];
  };
  Folder_Admin__r?: {
    Id: string;
    Name: string;
    Profile_Picture_URL__c?: string;
  };
}

interface AlbumContainerProps {
  cardData: FolderData[];
}

const ITEMS_PER_PAGE = 20;

const AlbumContainer: React.FC<AlbumContainerProps> = ({ cardData }) => {
  console.log(cardData, "carddata")
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [paginatedData, setPaginatedData] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const handleViewAlbum = (folderId: string) => {
    // Navigate to the new AlbumViewer component instead
    navigate(`/album/${folderId}`);
  };

  const handleAddPhotos = () => {
    navigate('/PhotoAlbumUpload');
  };

  const transformData = (folderData: FolderData[]) => {
    return folderData.map(folder => {
      let imageUrl = '';
      let hasValidImage = false;
      let creatorImage = '';
      let creatorName = '';
      let fileCount = 0;

      if (folder.Cover_Image_Url__c && folder.Cover_Image_Url__c.startsWith('http')) {
        imageUrl = folder.Cover_Image_Url__c;
        hasValidImage = true;
      }

      if (folder.Files__r && folder.Files__r.records.length > 0) {
        fileCount = folder.Files__r.records.length;
      }

      if (folder.Folder_Admin__r) {
        if (folder.Folder_Admin__r.Profile_Picture_URL__c &&
          folder.Folder_Admin__r.Profile_Picture_URL__c.startsWith('http')) {
          creatorImage = folder.Folder_Admin__r.Profile_Picture_URL__c;
        }
        creatorName = folder.Folder_Admin__r.Name || 'Unknown';
      }

      const lastUpdated = new Date(folder.LastModifiedDate);
      const formattedDate = `${lastUpdated.getDate()} ${lastUpdated.toLocaleString('default', { month: 'short' })}, ${lastUpdated.getFullYear()}`;

      return {
        id: folder.Id,
        title: folder.Name,
        Lastupdated: formattedDate,
        imageUrl: imageUrl,
        hasValidImage: hasValidImage,
        userName: creatorName || 'Unknown',
        creatorImage: creatorImage,
        collaborators: folder.Collaborators_Count__c,
        fileCount: fileCount
      };
    });
  };

  useEffect(() => {
    setLoading(true);

    // Use setTimeout to ensure the loader is visible
    const timer = setTimeout(() => {
      if (cardData?.length) {
        const transformedData = transformData(cardData);
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        setPaginatedData(transformedData.slice(startIndex, endIndex));
        setTotalPages(Math.ceil(transformedData.length / ITEMS_PER_PAGE));
      } else {
        setPaginatedData([]);
        setTotalPages(1);
      }
      setLoading(false);
    }, 500); // Add a small delay to ensure loader is visible

    return () => clearTimeout(timer);
  }, [cardData, currentPage]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  return (
    <div>
      <Box width="100%" display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="400px">
            <KenLoader />
          </Box>
        ) : (
          <>
            {(!cardData || cardData.length === 0) ? (
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                sx={{
                  height: { xs: 'auto', sm: 'calc(100vh - 120px)' },
                  minHeight: { xs: '60vh', sm: 'auto' },
                  textAlign: 'center',
                  padding: { xs: 2, sm: 3 }
                }}
              >
                <FolderIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: '#123A6F', opacity: 0.6, mb: 2 }} />
                <Typography variant="h6" color="#373A45" mb={1}>
                  No albums available
                </Typography>
                <Typography variant="body2" color="#6F7787" mb={3}>
                  Start by uploading your first image
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddPhotos}
                  sx={{
                    backgroundColor: '#123A6F',
                    '&:hover': { backgroundColor: '#0A2851' },
                    borderRadius: 2,
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Add photos
                </Button>
              </Box>
            ) : (
              <Paper sx={{
                padding: { xs: 1, sm: 2 },
                border: "none",
                boxShadow: "none",
                width: '100%'
              }}>
                <Grid container spacing={2}>
                  {paginatedData.map((item) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                      <Paper
                        elevation={2}
                        sx={{
                          borderRadius: 3,
                          padding: { xs: 1.5, sm: 2 },
                          backgroundColor: "#F6F8FB",
                          border: "none",
                          boxShadow: "none",
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                          }
                        }}
                        onClick={() => handleViewAlbum(item.id)}
                      >
                        <Box display="flex" alignItems="center" mb={0.5}>
                          <Typography
                            fontWeight={500}
                            color={"#373A45"}
                            fontSize={{ xs: 13, sm: 14 }}
                            sx={{ wordBreak: 'break-word' }}
                          >
                            {item.title}
                          </Typography>
                        </Box>

                        <Box display="flex" alignItems="center" mb={1}>
                          <Typography
                            fontWeight={400}
                            color={"#373A45"}
                            fontSize={{ xs: 12, sm: 14 }}
                          >
                            Last updated on: {item.Lastupdated}
                          </Typography>
                        </Box>


                        {item.hasValidImage ? (
                          <Box
                            component="img"
                            src={item.imageUrl}
                            alt={item.title}
                            sx={{
                              width: '100%',
                              height: { xs: 140, sm: 180 },
                              objectFit: 'cover',
                              borderRadius: 2,
                              mb: 1.5,
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                const fallbackElement = document.createElement('div');
                                fallbackElement.style.display = 'flex';
                                fallbackElement.style.justifyContent = 'center';
                                fallbackElement.style.alignItems = 'center';
                                fallbackElement.style.height = '180px';
                                fallbackElement.style.backgroundColor = '#F0F4F8';
                                fallbackElement.style.borderRadius = '8px';
                                fallbackElement.style.marginBottom = '12px';

                                const folderIconElement = document.createElement('div');
                                folderIconElement.innerHTML = `
                                  <svg style="width: 64px; height: 64px; color: #123A6F; opacity: 0.7;" viewBox="0 0 24 24">
                                    <path fill="currentColor" d="M10 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2h-8l-2-2z"></path>
                                  </svg>
                                `;

                                fallbackElement.appendChild(folderIconElement);
                                parent.appendChild(fallbackElement);
                              }
                            }}
                          />
                        ) : (
                          <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                              width: '100%',
                              height: { xs: 140, sm: 180 },
                              backgroundColor: '#FFFFFF',
                              borderRadius: 2,
                              mb: 1.5,
                            }}
                          >
                            <FolderIcon sx={{
                              fontSize: { xs: 48, sm: 64 },
                              color: '#123A6F',
                              opacity: 0.7
                            }} />
                          </Box>
                        )}

                        <Box
                          display="flex"
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          flexDirection={{ xs: 'column', sm: 'row' }}
                          justifyContent="space-between"
                          mt={1}
                          gap={{ xs: 1, sm: 0 }}
                        >
                          <Box display="flex" alignItems="center">
                            {item.creatorImage ? (
                              <Box
                                component="img"
                                src={item.creatorImage}
                                alt={item.userName}
                                sx={{ width: 30, height: 30, borderRadius: '50%', mr: 1 }}
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = 'none';
                                  const parent = target.parentElement;
                                  if (parent) {
                                    // Using MUI's PersonIcon directly
                                    const fallbackElement = document.createElement('div');
                                    fallbackElement.style.display = 'flex';
                                    fallbackElement.style.justifyContent = 'center';
                                    fallbackElement.style.alignItems = 'center';
                                    fallbackElement.style.width = '30px';
                                    fallbackElement.style.height = '30px';
                                    fallbackElement.style.backgroundColor = '#E3E8EF';
                                    fallbackElement.style.borderRadius = '50%';
                                    fallbackElement.style.marginRight = '8px';

                                    const personIconElement = document.createElement('div');
                                    personIconElement.innerHTML = `
                                      <svg style="width: 20px; height: 20px; color: #123A6F;" viewBox="0 0 24 24">
                                        <path fill="currentColor" d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path>
                                      </svg>
                                    `;

                                    fallbackElement.appendChild(personIconElement);
                                    parent.appendChild(fallbackElement);
                                  }
                                }}
                              />
                            ) : (
                              <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                sx={{
                                  width: 30,
                                  height: 30,
                                  backgroundColor: '#E3E8EF',
                                  borderRadius: '50%',
                                  mr: 1
                                }}
                              >
                                <PersonIcon sx={{ fontSize: 20, color: '#123A6F' }} />
                              </Box>
                            )}
                            <Typography
                              fontWeight={500}
                              color={"#373A45"}
                              fontSize={{ xs: 13, sm: 14 }}
                            >
                              {item.userName}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <GroupOutlinedIcon fontSize="small" />
                            <Typography
                              fontSize={{ xs: 13, sm: 14 }}
                              fontWeight={400}
                              color={"#373A45"}
                            >
                              {item.collaborators} collaborators
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>

                {totalPages > 1 && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    mt={3}
                    sx={{ overflowX: 'auto', py: 1, width: '100%' }}
                  >
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      variant="outlined"
                      shape="rounded"
                      siblingCount={window.innerWidth < 600 ? 0 : 2}
                      boundaryCount={window.innerWidth < 600 ? 0 : 1}
                      size={window.innerWidth < 600 ? 'small' : 'medium'}
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: '8px',
                          fontWeight: 400,
                          color: '#000',
                          border: '1px solid #E0E0E0',
                          '&:hover': {
                            backgroundColor: '#F5F5F5',
                          },
                        },
                        '& .MuiPaginationItem-page.Mui-selected': {
                          backgroundColor: '#123A6F',
                          color: '#fff',
                          fontWeight: 500,
                          '&:hover': {
                            backgroundColor: '#123A6F',
                          },
                        },
                        '& .MuiPaginationItem-ellipsis': {
                          color: '#000',
                        },
                        '& .MuiPaginationItem-previousNext': {
                          fontWeight: 400,
                          color: '#000',
                        },
                        '& .MuiPaginationItem-previousNext.Mui-disabled': {
                          color: '#C0C0C0',
                        },
                      }}
                      showFirstButton={false}
                      showLastButton={false}
                    />
                  </Box>
                )}
              </Paper>
            )}
          </>
        )}
      </Box>
    </div>
  );
};

export default AlbumContainer;
